import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import {
  getApiErrorCode,
  getApiErrorDetailId,
  toastApiError,
} from "../../../api/apiError"
import {
  useAddArticleSubstance,
  useArticleSuppliers,
} from "../../../api/articlesApi"
import { ArticlePicker } from "../../customer-requests/processing/ArticlePicker"
import { SubstancePicker } from "./SubstancePicker"

const FIELD_CLASS = "border-grey4 bg-grey2 text-white"

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <label htmlFor={htmlFor} className="text-[11.5px] font-semibold text-grey5">
      {children}
    </label>
  )
}

export interface LinkSubstanceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fixed when opened from an article's detail page — the dialog then only
   *  needs to pick a substance (and, once that's fixed, a supplier from this
   *  article's own suppliers). Exactly one of `articleId`/`substanceId` is
   *  given by any one caller; the other is what the user picks inside. */
  articleId?: string
  /** Fixed when opened from a substance's detail page — the dialog then
   *  needs to pick an article first, and only then can the supplier select
   *  populate (that list depends on whichever article gets picked). */
  substanceId?: string
  onLinked?: () => void
  /** The (article, supplier, substance) link already exists: take the user to it
   *  rather than leaving them on a create form that can only fail again. */
  /** Called with the id of the LINK that already exists, so the caller can
   *  open that row for editing rather than navigate to the substance. */
  onExistingLink?: (linkId: string) => void
}

/**
 * Links an EXISTING substance to an EXISTING article for a REQUIRED supplier
 * (one of the article's own already-linked suppliers). Modeled on
 * AddArticleDialog.tsx's shape: the body lives in a separate inner component
 * so all local state resets when DialogContent unmounts on close — same
 * remount pattern as CreateCustomerModal/CreateSubstanceModal.
 */
export function LinkSubstanceDialog({
  open,
  onOpenChange,
  articleId,
  substanceId,
  onLinked,
  onExistingLink,
}: LinkSubstanceDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <LinkSubstanceDialogBody
          articleId={articleId}
          substanceId={substanceId}
          onLinked={onLinked}
          onOpenChange={onOpenChange}
          onExistingLink={onExistingLink}
        />
      </DialogContent>
    </Dialog>
  )
}

function LinkSubstanceDialogBody({
  articleId,
  substanceId,
  onLinked,
  onOpenChange,
  onExistingLink,
}: {
  articleId?: string
  substanceId?: string
  onLinked?: () => void
  onOpenChange: (open: boolean) => void
  /** The link already exists: take the user to it rather than leaving them on a
   *  create form that can only fail again. */
  onExistingLink?: (linkId: string) => void
}) {
  const { translate } = useTranslation()
  const { mutate: addArticleSubstance, isPending } = useAddArticleSubstance()

  // Only populated (relevant) on the side that ISN'T fixed by the caller.
  const [pickedSubstanceId, setPickedSubstanceId] = useState<string | null>(
    null,
  )
  const [substanceLabel, setSubstanceLabel] = useState("")
  const [pickedArticleId, setPickedArticleId] = useState<string | null>(null)
  const [articleLabel, setArticleLabel] = useState("")

  const [supplierId, setSupplierId] = useState<string | undefined>(undefined)
  const [concentrationPct, setConcentrationPct] = useState("")

  const [conflictMessage, setConflictMessage] = useState<string | null>(null)
  // The existing link, read off the 409 body — the row the user actually wants.
  const [conflictLinkId, setConflictLinkId] = useState<string | null>(null)
  const [supplierErrorMessage, setSupplierErrorMessage] = useState<
    string | null
  >(null)
  const [concentrationError, setConcentrationError] = useState<string | null>(
    null,
  )

  const resolvedArticleId = articleId ?? pickedArticleId ?? null
  const resolvedSubstanceId = substanceId ?? pickedSubstanceId ?? null

  // Fixed-article case: fetches immediately, list is stable for the dialog's
  // lifetime. Fixed-substance case: disabled (enabled: !!id under the hood)
  // until the user picks an article, then refetches for whichever one that is.
  const { data: supplierLinks = [], isSuccess: suppliersLoaded } =
    useArticleSuppliers(resolvedArticleId)
  const supplierOptions = supplierLinks.filter((s) => s.active)

  const soleSupplierId =
    supplierOptions.length === 1 ? supplierOptions[0].supplierId : undefined

  // Two jobs, one pass. Clearing is load-bearing: a stale supplier id from a
  // previously-picked article must never silently submit against a newly-picked
  // one. And when the article has exactly one supplier there is nothing to
  // choose, so it is pre-selected rather than making the user open a
  // single-entry dropdown — `soleSupplierId` being undefined IS the clear.
  //
  // Both deps are primitives on purpose: `supplierOptions` is rebuilt by
  // `.filter()` every render, so depending on the array itself would re-run
  // forever. `resolvedArticleId` earns its place separately from the id it
  // derives — two articles can share the same sole supplier, and switching
  // between them must still re-apply rather than leave the field cleared.
  //
  // No stale-article guard is needed: `useArticleSuppliers` sets no
  // placeholderData and keys on the article id, so `supplierLinks` falls back
  // to [] until the newly-picked article's own fetch settles.
  useEffect(() => {
    setSupplierId(soleSupplierId)
    setSupplierErrorMessage(null)
  }, [resolvedArticleId, soleSupplierId])

  const canSubmit =
    !!resolvedSubstanceId && !!resolvedArticleId && !!supplierId && !isPending

  function handleSubmit() {
    if (!resolvedArticleId || !resolvedSubstanceId || !supplierId) return
    setConflictMessage(null)
    setSupplierErrorMessage(null)
    setConcentrationError(null)

    const trimmedConcentration = concentrationPct.trim()
    const parsedConcentration = trimmedConcentration
      ? Number(trimmedConcentration)
      : undefined

    // type="number" still lets onChange fire with an incomplete/invalid
    // intermediate value (e.g. a lone "-" or "."), which Number() turns into
    // NaN — block the submit rather than send that to the API.
    if (
      parsedConcentration !== undefined &&
      (Number.isNaN(parsedConcentration) ||
        parsedConcentration < 0 ||
        parsedConcentration > 100)
    ) {
      setConcentrationError(
        translate("MC_MD_LINK_SUBSTANCE_CONCENTRATION_INVALID"),
      )
      return
    }

    addArticleSubstance(
      {
        articleId: resolvedArticleId,
        substanceId: resolvedSubstanceId,
        supplierId,
        concentrationPct: parsedConcentration,
        // `exemption`/`exemptionCode` are deliberately not sent. A RoHS Annex
        // III/IV exemption is scoped to a category of EEE, so whether one
        // applies is a property of the PRODUCT, not the article: the same
        // article can sit in an electronic product (RoHS applies) and a
        // non-electronic one (RoHS does not apply at all), and one
        // article-level value cannot be right for both. The backend's
        // ArticleSubstanceCreateIn defaults them to False/None, and the
        // declaration and import paths still populate the columns with what a
        // supplier actually declared.
      },
      {
        onSuccess: () => {
          onLinked?.()
          onOpenChange(false)
        },
        onError: (err) => {
          const code = getApiErrorCode(err)
          if (code === "SUBSTANCE_ALREADY_LINKED") {
            // Inline, not a toast — the user needs to see this while still
            // looking at the form to act on it (adjust supplier or cancel).
            setConflictMessage(translate("MC_MD_LINK_SUBSTANCE_CONFLICT"))
            setConflictLinkId(getApiErrorDetailId(err, "link_id"))
          } else if (code === "SUPPLIER_NOT_LINKED_TO_ARTICLE") {
            // Rare/near-unreachable — the select is already populated FROM
            // this article's active suppliers — but the link could have been
            // deactivated in another tab mid-session, so handle it anyway.
            setSupplierErrorMessage(
              translate("MC_MD_LINK_SUBSTANCE_SUPPLIER_INVALID"),
            )
          } else {
            // A >=500 is already toasted once by the global interceptor.
            toastApiError(err, translate, "MC_API_ERROR_GENERIC")
          }
        },
      },
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{translate("MC_MD_LINK_SUBSTANCE_TITLE")}</DialogTitle>
        <DialogDescription>
          {translate("MC_MD_LINK_SUBSTANCE_DESC")}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3 p-1">
        {articleId ? (
          <div className="flex flex-col gap-1.5">
            <FieldLabel>{translate("MC_MD_PANEL_COL_SUBSTANCE")}</FieldLabel>
            <SubstancePicker
              triggerLabel={
                substanceLabel ||
                translate("MC_MD_LINK_SUBSTANCE_PICK_SUBSTANCE")
              }
              onSelect={(id, label) => {
                setPickedSubstanceId(id)
                setSubstanceLabel(label)
              }}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <FieldLabel>{translate("MC_MD_PANEL_COL_ARTICLE")}</FieldLabel>
            <ArticlePicker
              triggerLabel={
                articleLabel || translate("MC_MD_LINK_SUBSTANCE_PICK_ARTICLE")
              }
              onSelect={(id, articleNumber) => {
                setPickedArticleId(id)
                setArticleLabel(articleNumber)
              }}
              className="h-9 w-full justify-between px-3 text-[13px]"
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="link-substance-supplier">
            {translate("MC_MD_PANEL_COL_SUPPLIER")}
          </FieldLabel>
          <Select
            value={supplierId}
            onValueChange={setSupplierId}
            disabled={!resolvedArticleId}
          >
            <SelectTrigger id="link-substance-supplier" className={FIELD_CLASS}>
              <SelectValue
                placeholder={translate(
                  "MC_MD_LINK_SUBSTANCE_SUPPLIER_PLACEHOLDER",
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {supplierOptions.map((s) => (
                <SelectItem key={s.supplierId} value={s.supplierId}>
                  {s.supplierName ?? s.supplierId}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!resolvedArticleId && (
            <span className="text-[11px] text-grey5">
              {translate("MC_MD_LINK_SUBSTANCE_SUPPLIER_NEEDS_ARTICLE")}
            </span>
          )}
          {/* Gated on the query having SETTLED, not just on the array being
              empty: `supplierLinks` defaults to [] while the fetch is in
              flight, so an ungated check flashes this every time the dialog
              opens. Without a supplier the submit stays disabled forever, so
              the user needs to be told why rather than left guessing. */}
          {resolvedArticleId &&
            suppliersLoaded &&
            supplierOptions.length === 0 && (
              <span className="text-[11px] text-grey5">
                {translate("MC_MD_LINK_SUBSTANCE_SUPPLIER_NONE")}
              </span>
            )}
          {supplierErrorMessage && (
            <span role="alert" className="text-[11px] text-red1">
              {supplierErrorMessage}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="link-substance-concentration">
            {translate("MC_MD_PANEL_COL_CONC")}
          </FieldLabel>
          <Input
            id="link-substance-concentration"
            type="number"
            step="any"
            min={0}
            value={concentrationPct}
            aria-invalid={!!concentrationError}
            onChange={(e) => {
              setConcentrationPct(e.target.value)
              if (concentrationError) setConcentrationError(null)
            }}
            className={FIELD_CLASS}
          />
          {concentrationError && (
            <span role="alert" className="text-[11px] text-red1">
              {concentrationError}
            </span>
          )}
        </div>

        {conflictMessage && (
          <div
            role="alert"
            className="rounded-md border border-yellow1/30 bg-yellow1/10 p-2.5 text-[12px] text-white"
          >
            {conflictMessage}
            {/* The user came here to record this substance; "it already exists" is
                only half an answer. Offer the row itself so the next click edits
                the entry instead of retrying a create that cannot succeed. */}
            {onExistingLink && conflictLinkId && (
              <button
                type="button"
                className="mt-1.5 block font-medium text-yellow1 hover:underline"
                onClick={() => onExistingLink(conflictLinkId)}
              >
                {translate("MC_MD_LINK_SUBSTANCE_OPEN_EXISTING")}
              </button>
            )}
          </div>
        )}
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          {translate("MC_MD_EDIT_CANCEL")}
        </Button>
        <Button
          className="bg-yellow1 text-black hover:bg-yellow2"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {translate("MC_MD_LINK_SUBSTANCE_SUBMIT")}
        </Button>
      </DialogFooter>
    </>
  )
}
